import { useEffect, useId, useState } from 'react';
import { CenterScreenModal } from '../atoms';
import { useUserLanguage } from '@/customHooks';
import type {
  InventoryFormModalTexts,
  InventoryItemRow,
  InventoryInspectionItemStatus,
  InventoryInspectionItemProvider,
  InventoryItemPresence,
} from '@/types/custom-types';
import textsData from '@/textData/InventoryItemsTableTexts.json';
import { isInventoryItemFilled, isRequiredInventoryItemComplete } from '@/helpers/inventoryCatalog';

type InventoryItemsTableProps = {
  items: InventoryItemRow[];
  onChange: (items: InventoryItemRow[]) => void;
  texts: InventoryFormModalTexts;
  isReadOnly: boolean;
  catalogSuggestions: string[];
};

const createClientId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const InventoryItemsTable = ({
  items,
  onChange,
  texts,
  isReadOnly,
  catalogSuggestions,
}: InventoryItemsTableProps) => {
  const userLanguage = useUserLanguage();
  const localTexts = userLanguage === 'es' ? textsData.es : textsData.en;
  const datalistId = useId();
  const [newFilePreviewUrls, setNewFilePreviewUrls] = useState<Record<string, string[]>>({});
  const [viewerState, setViewerState] = useState<{
    isOpen: boolean;
    urls: string[];
    selectedIndex: number;
  }>({
    isOpen: false,
    urls: [],
    selectedIndex: 0,
  });

  useEffect(() => {
    const createdUrls: string[] = [];
    const nextPreviewMap: Record<string, string[]> = {};

    items.forEach((item) => {
      const key = item.clientId;
      const urls = (item.newFiles ?? []).map((file) => {
        const url = URL.createObjectURL(file);
        createdUrls.push(url);
        return url;
      });
      nextPreviewMap[key] = urls;
    });

    setNewFilePreviewUrls(nextPreviewMap);

    return () => {
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [items]);

  const updateItem = (clientId: string, patch: Partial<InventoryItemRow>) => {
    onChange(
      items.map((item) => {
        if (item.clientId !== clientId) return item;
        const next = { ...item, ...patch };
        next.isReviewed = isInventoryItemFilled(next);
        return next;
      }),
    );
  };

  const handleAddItem = () => {
    if (isReadOnly) return;
    onChange([
      ...items,
      {
        clientId: createClientId(),
        description: '',
        quantity: 1,
        status: 'NOT_EVALUATED',
        notes: '',
        isReviewed: false,
        isRequired: false,
        isPresentInProperty: null,
        providedBy: null,
        existingFiles: [],
        newFiles: [],
        shouldReplaceFiles: false,
      },
    ]);
  };

  const handleRemoveItem = (clientId: string) => {
    if (isReadOnly) return;
    onChange(items.filter((item) => item.clientId !== clientId));
  };

  const handleFilesSelected = (clientId: string, files: FileList | null) => {
    if (!files || files.length === 0 || isReadOnly) return;
    const selectedFiles = Array.from(files).filter(
      (file) =>
        file.type.startsWith('image/') ||
        file.type === '' ||
        /\.(jpe?g|png|webp|heic|heif|gif|bmp|tiff?|avif)$/i.test(file.name),
    );
    if (selectedFiles.length === 0) return;
    onChange(
      items.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              newFiles: [...(item.newFiles ?? []), ...selectedFiles],
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const handleRemoveExistingFile = (clientId: string, fileId: string) => {
    if (isReadOnly) return;
    onChange(
      items.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              existingFiles: (item.existingFiles ?? []).filter((file) => file.id !== fileId),
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const handleRemoveNewFile = (clientId: string, fileIndex: number) => {
    if (isReadOnly) return;
    onChange(
      items.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              newFiles: (item.newFiles ?? []).filter((_, newIdx) => newIdx !== fileIndex),
              shouldReplaceFiles: true,
            }
          : item,
      ),
    );
  };

  const openImageViewer = (item: InventoryItemRow, selectedIndex: number) => {
    const urls = [
      ...(item.existingFiles ?? []).map((file) => file.url),
      ...(newFilePreviewUrls[item.clientId] ?? []),
    ];
    if (urls.length === 0) return;
    setViewerState({ isOpen: true, urls, selectedIndex });
  };

  const photoCount = (item: InventoryItemRow) =>
    (item.existingFiles ?? []).length + (item.newFiles ?? []).length;

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="px-2 py-2" />
              <th className="px-2 py-2">{texts.labels.itemDescription}</th>
              <th className="px-2 py-2">{texts.labels.quantity}</th>
              <th className="px-2 py-2">{localTexts.presentLabel}</th>
              <th className="px-2 py-2">{localTexts.providerLabel}</th>
              <th className="px-2 py-2">{localTexts.qualityLabel}</th>
              <th className="hidden px-2 py-2 md:table-cell">{texts.labels.itemNotes}</th>
              <th className="px-2 py-2">{localTexts.photos}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const count = photoCount(item);
              const incompleteRequired = !isRequiredInventoryItemComplete(item);
              return (
                <tr
                  key={item.clientId}
                  className={`border-t align-middle ${
                    incompleteRequired
                      ? 'border-amber-200 bg-amber-50/50'
                      : 'border-slate-200'
                  }`}
                >
                  <td className="w-9 px-2 py-1.5">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#4A9462]"
                      checked={item.isReviewed}
                      disabled={isReadOnly}
                      onChange={(event) =>
                        updateItem(item.clientId, { isReviewed: event.target.checked })
                      }
                    />
                  </td>
                  <td className="min-w-[180px] px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        list={datalistId}
                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#4A9462]"
                        value={item.description}
                        placeholder={localTexts.itemPlaceholder}
                        readOnly={isReadOnly}
                        onChange={(event) =>
                          updateItem(item.clientId, { description: event.target.value })
                        }
                      />
                      {item.isRequired ? (
                        <span
                          className="shrink-0 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600"
                          title={localTexts.requiredBadge}
                        >
                          *
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="w-20 px-2 py-1.5">
                    <input
                      type="number"
                      min={0}
                      className="w-16 rounded-lg border border-slate-200 bg-white px-2 py-2 text-center text-sm outline-none focus:border-[#4A9462] disabled:bg-slate-50 disabled:text-slate-400"
                      value={item.quantity === '' ? '' : item.quantity}
                      readOnly={isReadOnly}
                      disabled={isReadOnly || item.isPresentInProperty !== 'YES'}
                      onChange={(event) =>
                        updateItem(item.clientId, {
                          quantity:
                            event.target.value === '' ? '' : Number(event.target.value),
                        })
                      }
                    />
                  </td>
                  <td className="w-[140px] px-2 py-1.5">
                    <select
                      className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm outline-none focus:border-[#4A9462]"
                      value={item.isPresentInProperty ?? 'UNSET'}
                      disabled={isReadOnly}
                      onChange={(event) => {
                        const value = event.target.value;
                        const isPresentInProperty =
                          value === 'YES' || value === 'NO' || value === 'NOT_NEEDED'
                            ? (value as InventoryItemPresence)
                            : null;
                        updateItem(item.clientId, {
                          isPresentInProperty,
                          providedBy: isPresentInProperty === 'NO' ? item.providedBy : null,
                          quantity:
                            isPresentInProperty === 'YES' ? item.quantity : '',
                          status:
                            isPresentInProperty === 'NO'
                              ? 'MISSING'
                              : item.status === 'MISSING'
                                ? 'NOT_EVALUATED'
                                : item.status,
                        });
                      }}
                    >
                      <option value="UNSET">{localTexts.presentOptions.UNSET}</option>
                      <option value="YES">{localTexts.presentOptions.YES}</option>
                      <option value="NO">{localTexts.presentOptions.NO}</option>
                      <option value="NOT_NEEDED">{localTexts.presentOptions.NOT_NEEDED}</option>
                    </select>
                  </td>
                  <td className="w-[160px] px-2 py-1.5">
                    <select
                      className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm outline-none focus:border-[#4A9462] disabled:bg-slate-50 disabled:text-slate-400"
                      value={item.providedBy ?? 'UNSET'}
                      disabled={isReadOnly || item.isPresentInProperty !== 'NO'}
                      onChange={(event) =>
                        updateItem(item.clientId, {
                          providedBy:
                            event.target.value === 'OWNER' ||
                            event.target.value === 'LIVE4LIFE' ||
                            event.target.value === 'UNKNOWN'
                              ? (event.target.value as InventoryInspectionItemProvider)
                              : null,
                        })
                      }
                    >
                      <option value="UNSET">{localTexts.providerOptions.UNSET}</option>
                      <option value="OWNER">{localTexts.providerOptions.OWNER}</option>
                      <option value="LIVE4LIFE">{localTexts.providerOptions.LIVE4LIFE}</option>
                      <option value="UNKNOWN">{localTexts.providerOptions.UNKNOWN}</option>
                    </select>
                  </td>
                  <td className="w-[130px] px-2 py-1.5">
                    <select
                      className={`w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm outline-none focus:border-[#4A9462] ${
                        item.status === 'EXCELLENT' ? 'font-semibold text-emerald-700' : ''
                      }`}
                      value={item.status ?? 'NOT_EVALUATED'}
                      disabled={isReadOnly}
                      onChange={(event) =>
                        updateItem(item.clientId, {
                          status: event.target.value as InventoryInspectionItemStatus,
                        })
                      }
                    >
                      {(
                        Object.keys(localTexts.statusOptions) as InventoryInspectionItemStatus[]
                      ).map((status) => (
                        <option key={status} value={status}>
                          {localTexts.statusOptions[status]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="hidden min-w-[160px] px-2 py-1.5 md:table-cell">
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-sm outline-none focus:border-[#4A9462]"
                      value={item.notes ?? ''}
                      placeholder={localTexts.notesPlaceholder}
                      readOnly={isReadOnly}
                      onChange={(event) =>
                        updateItem(item.clientId, { notes: event.target.value })
                      }
                    />
                  </td>
                  <td className="w-[130px] px-2 py-1.5">
                    {!isReadOnly ? (
                      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600 hover:border-[#4A9462] hover:text-[#3c7c51]">
                        {localTexts.photos}
                        {count > 0 ? ` (${count})` : ''}
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(event) => {
                            handleFilesSelected(item.clientId, event.target.files);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    ) : (
                      <span className="text-xs text-slate-500">
                        {count > 0 ? `${localTexts.photos} (${count})` : localTexts.noPhotos}
                      </span>
                    )}
                    {count > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(item.existingFiles ?? []).map((file, fileIndex) => (
                          <button
                            key={file.id}
                            type="button"
                            className="relative"
                            onClick={() => openImageViewer(item, fileIndex)}
                          >
                            <img
                              src={file.url}
                              alt={localTexts.attachedFiles}
                              className="h-10 w-10 rounded object-cover"
                            />
                          </button>
                        ))}
                        {(item.newFiles ?? []).map((file, fileIndex) => {
                          const preview = newFilePreviewUrls[item.clientId]?.[fileIndex];
                          const selectedIndex = (item.existingFiles ?? []).length + fileIndex;
                          return preview ? (
                            <button
                              key={`${file.name}-${fileIndex}`}
                              type="button"
                              onClick={() => openImageViewer(item, selectedIndex)}
                            >
                              <img
                                src={preview}
                                alt={file.name}
                                className="h-10 w-10 rounded object-cover"
                              />
                            </button>
                          ) : null;
                        })}
                      </div>
                    )}
                    {!isReadOnly && count > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(item.existingFiles ?? []).map((file) => (
                          <button
                            key={`rm-${file.id}`}
                            type="button"
                            className="text-[11px] text-slate-400 hover:text-red-500"
                            onClick={() => handleRemoveExistingFile(item.clientId, file.id)}
                          >
                            {localTexts.remove}
                          </button>
                        ))}
                        {(item.newFiles ?? []).map((_, fileIndex) => (
                          <button
                            key={`rm-new-${fileIndex}`}
                            type="button"
                            className="text-[11px] text-slate-400 hover:text-red-500"
                            onClick={() => handleRemoveNewFile(item.clientId, fileIndex)}
                          >
                            {localTexts.remove}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="w-10 px-1 py-1.5">
                    {!isReadOnly && !item.isRequired && (
                      <button
                        type="button"
                        title={texts.buttons.removeItem}
                        className="rounded-lg px-2 py-1 text-base text-slate-400 hover:bg-red-50 hover:text-red-500"
                        onClick={() => handleRemoveItem(item.clientId)}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <datalist id={datalistId}>
          {catalogSuggestions.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      </div>

      {!isReadOnly && (
        <div className="mt-2 flex items-center gap-2 px-2">
          <button
            type="button"
            onClick={handleAddItem}
            className="rounded-lg border border-dashed border-[#bcd6c6] px-3 py-2 text-sm font-semibold text-[#3c7c51] hover:bg-[#eef5f0]"
          >
            ＋ {texts.buttons.addItem}
          </button>
          <span className="text-xs text-slate-400">{texts.buttons.addItemHint}</span>
        </div>
      )}

      <CenterScreenModal
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState((prev) => ({ ...prev, isOpen: false }))}
      >
        <div className="w-[90vw] max-w-5xl space-y-3">
          <div className="flex h-[70vh] items-center justify-center rounded bg-black p-2">
            {viewerState.urls[viewerState.selectedIndex] && (
              <img
                src={viewerState.urls[viewerState.selectedIndex]}
                alt="Vista ampliada"
                className="max-h-full max-w-full object-contain"
              />
            )}
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {viewerState.urls.map((url, index) => (
              <button
                type="button"
                key={`${url}-${index}`}
                onClick={() => setViewerState((prev) => ({ ...prev, selectedIndex: index }))}
                className={`rounded border-2 ${
                  index === viewerState.selectedIndex ? 'border-blue-600' : 'border-transparent'
                }`}
              >
                <img src={url} alt={`preview-${index}`} className="h-16 w-16 rounded object-cover" />
              </button>
            ))}
          </div>
        </div>
      </CenterScreenModal>
    </>
  );
};
